import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Textarea } from '@/common/ui/textarea'
import { Label } from '@/common/ui/label'
import { AlertTriangle, Settings } from 'lucide-react'
import { AdminUpdateWithdrawStatusDtoStatus } from '@/schema'
import { useMutateWithdrawTransaction } from '../../../api/hooks/useMutateWithdrawTransaction'
import { Icons } from '@/common/ui/icons'

interface UpdateWithdrawStatusModalProps {
	id: string
	currentStatus: string
}

const statusLabels: Record<AdminUpdateWithdrawStatusDtoStatus, string> = {
	[AdminUpdateWithdrawStatusDtoStatus.PENDING]: 'รอดำเนินการ',
	[AdminUpdateWithdrawStatusDtoStatus.PROCESSING]: 'กำลังดำเนินการ',
	[AdminUpdateWithdrawStatusDtoStatus.COMPLETED]: 'สำเร็จ',
	[AdminUpdateWithdrawStatusDtoStatus.FAILED]: 'ไม่สำเร็จ',
	[AdminUpdateWithdrawStatusDtoStatus.CANCELED]: 'ยกเลิก',
	[AdminUpdateWithdrawStatusDtoStatus.EXPIRED]: 'หมดเวลา',
	[AdminUpdateWithdrawStatusDtoStatus.REFUNDED]: 'คืนเงินแล้ว',
}

const warningStatuses = [
	AdminUpdateWithdrawStatusDtoStatus.COMPLETED,
	AdminUpdateWithdrawStatusDtoStatus.FAILED,
	AdminUpdateWithdrawStatusDtoStatus.CANCELED,
]

export default function UpdateWithdrawStatusModal({
	id,
	currentStatus,
}: UpdateWithdrawStatusModalProps) {
	const [open, setOpen] = useState(false)
	const [selectedStatus, setSelectedStatus] = useState<string>('')
	const [note, setNote] = useState('')
	const { updateStatus } = useMutateWithdrawTransaction()

	const handleUpdateStatus = () => {
		if (!selectedStatus) return

		updateStatus.mutate(
			{ id, status: selectedStatus, note: note || undefined },
			{
				onSuccess: () => {
					setOpen(false)
					setSelectedStatus('')
					setNote('')
				},
			},
		)
	}

	const showWarning =
		selectedStatus &&
		warningStatuses.includes(
			selectedStatus as AdminUpdateWithdrawStatusDtoStatus,
		)

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className="border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100"
				>
					<Settings className="mr-2 h-4 w-4" />
					อัพเดทสถานะ
				</Button>
			</DialogTrigger>
			<DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md overflow-y-auto">
				<DialogHeader>
					<DialogTitle>อัพเดทสถานะการถอนเงิน</DialogTitle>
					<DialogDescription>
						เปลี่ยนสถานะธุรกรรมการถอนเงินเป็นสถานะอื่น
					</DialogDescription>
				</DialogHeader>

				{/* Critical Warning */}
				<div className="rounded-lg border-2 border-red-500 bg-red-50 p-4 dark:bg-red-950">
					<div className="flex items-start gap-3">
						<AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
						<div className="space-y-2 text-sm">
							<p className="font-bold text-red-900 dark:text-red-100">
								⚠️ คำเตือนสำคัญ
							</p>
							<p className="text-red-800 dark:text-red-200">
								การอัพเดทสถานะนี้
								<strong className="underline">ไม่ได้ทำธุรกรรมจริง</strong>
							</p>
							<p className="text-red-800 dark:text-red-200">
								คุณต้อง<strong>ตรวจสอบและดำเนินการด้วยตัวเอง</strong>
								ก่อนเปลี่ยนสถานะ เช่น ตรวจสอบว่าเงินออกจริง
								หรือทำรายการถอนเงินให้ผู้เล่นแล้ว
							</p>
						</div>
					</div>
				</div>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<Label htmlFor="current-status" className="text-sm font-medium">
							สถานะปัจจุบัน
						</Label>
						<div className="rounded-md border p-3 text-sm">
							{statusLabels[
								currentStatus as AdminUpdateWithdrawStatusDtoStatus
							] || currentStatus}
						</div>
					</div>

					<div className="space-y-2">
						<Label htmlFor="new-status" className="text-sm font-medium">
							สถานะใหม่ <span className="text-red-500">*</span>
						</Label>
						<Select value={selectedStatus} onValueChange={setSelectedStatus}>
							<SelectTrigger id="new-status">
								<SelectValue placeholder="เลือกสถานะ" />
							</SelectTrigger>
							<SelectContent>
								{Object.values(AdminUpdateWithdrawStatusDtoStatus).map(
									(status) => (
										<SelectItem key={status} value={status}>
											{statusLabels[status]}
										</SelectItem>
									),
								)}
							</SelectContent>
						</Select>
					</div>

					{showWarning && (
						<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
							<div className="flex items-start gap-2">
								<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
								<div className="space-y-1 text-sm">
									<p className="font-medium text-amber-800">คำเตือน</p>
									{selectedStatus ===
										AdminUpdateWithdrawStatusDtoStatus.COMPLETED && (
										<p className="text-amber-700">
											เป็นการบันทึกสถานะเท่านั้น
											<strong> ไม่ได้หักเครดิตจากผู้เล่น</strong>
											<br />
											กรุณาตรวจสอบให้แน่ใจว่าเงินออกจากระบบแล้วก่อนเปลี่ยนสถานะ
										</p>
									)}
									{selectedStatus ===
										AdminUpdateWithdrawStatusDtoStatus.FAILED && (
										<p className="text-amber-700">
											เป็นการบันทึกสถานะเท่านั้น
											<strong> ไม่ได้คืนเครดิตให้ผู้เล่น</strong>
											<br />
											หากต้องการคืนเครดิต ต้องกดปุ่ม &ldquo;คืนเงิน
											(Refund)&rdquo; แยกต่างหาก
										</p>
									)}
									{selectedStatus ===
										AdminUpdateWithdrawStatusDtoStatus.CANCELED && (
										<p className="text-amber-700">
											เป็นการบันทึกสถานะเท่านั้น
											<strong> ไม่ได้คืนเครดิตให้ผู้เล่น</strong>
											<br />
											หากต้องการคืนเครดิต ต้องใช้ปุ่ม &ldquo;ยกเลิกรายการ
											(Cancel)&rdquo; แยกต่างหาก
										</p>
									)}
								</div>
							</div>
						</div>
					)}

					<div className="space-y-2">
						<Label htmlFor="note" className="text-sm font-medium">
							หมายเหตุ (ถ้ามี)
						</Label>
						<Textarea
							id="note"
							placeholder="ระบุเหตุผลหรือหมายเหตุเพิ่มเติม..."
							value={note}
							onChange={(e) => setNote(e.target.value)}
							className="min-h-[80px]"
						/>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => {
							setOpen(false)
							setSelectedStatus('')
							setNote('')
						}}
						disabled={updateStatus.isPending}
					>
						ยกเลิก
					</Button>
					<Button
						onClick={handleUpdateStatus}
						disabled={!selectedStatus || updateStatus.isPending}
						className="bg-purple-600 hover:bg-purple-700"
					>
						{updateStatus.isPending ? (
							<>
								<Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
								กำลังดำเนินการ...
							</>
						) : (
							'ยืนยันการเปลี่ยนแปลง'
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
